import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, ReplaySubject } from 'rxjs';
import { concatMap, filter, map, take } from 'rxjs/operators';

import { SpAuthService } from './utility-services/auth.service';

import { profileUrl, profileCleanKeys } from './constants';
import { EmployeePackage, EmployeePackageResponse, AuthId } from '../models';
import * as utils from './utils/service.utils';

@Injectable({ providedIn: 'root' })
export class EmployeeProfileService {
  /*** Observable cache for Employee Profile. */
  employeeProfileSubject = new ReplaySubject<EmployeePackage>(1);

  /*** Observable used to connect to observable cache. */
  employeeProfile$ = this.employeeProfileSubject.asObservable();

  /*** Observable used to get the api credentials for the remaining services. */
  apiCreds$ = this.employeeProfile$.pipe(
    map(({ personal }) => ({ id: personal.employeeId, bcl: personal.bclCode }))
  );

  userMail$ = this.employeeProfile$.pipe(map(({ personal }) => ({ email: personal.email })));

  constructor(private auth: SpAuthService, private http: HttpClient) {
    /** Refresh Employee Profile Data from API */
    this.refresh();
  }

  /**
   ** Fetch Data from api of newly created url string.
   * 1. Build URL string.
   * ___
   *
   * 2. Fetch URL string and return Observable of Response Data.
   * ___
   * @param authId - Auth Credentials necessary for Api Calls
   */
  private fetchData(authId?: AuthId): Observable<EmployeePackageResponse> {
    //* 1.
    const url = `${profileUrl}/${authId?.bcl}/${authId?.secLoginId}`;
    //* 2.
    return this.http.get<EmployeePackageResponse>(url);
  }

  /**
   ** Rename raw API property names into Service-able property names
   * 1. Extract usable EmployeeProfile data from combined API responses
   * ___
   * 2. Rename filtered EmployeeProfile Data with Clean Profile Keys.
   * ___
   * 3. Return cleaned EmployeeProfile Data
   * ___
   * @param {rawEmployeeData} rawEmployeeData - raw data coming from Employee Service
   */
  private formatEmployeeProfile(rawEmployeeData: EmployeePackageResponse): EmployeePackage {
    //* 1.
    const { success, errorMessage, ...filteredData } = rawEmployeeData;
    //* 2.
    const cleanedEmployeeData = utils.cleanApiKeys(profileCleanKeys, filteredData);
    //* 3.
    const ret = utils.keysToCamelCase(cleanedEmployeeData) as EmployeePackage;
    if (ret?.personal) {
      ret.personal.birthDate = ret.personal?.birthDate
        ? ret.personal.birthDate?.split('T')?.[0]
        : '';
    }

    return ret;
  }

  /**
   *? Fetch data using AuthId passed from SPAuthService.
   * ___
   * 1. Fetch httpRequest using authId's passed from SPAuthService.
   *    - This returns the rawEmployeeProfileData observable.
   * ___
   * 2. Map to format rawEmployeeData into EmployeeProfile Type.
   * ___
   * @param {authId} authId - AuthId coming from Auth0 claims
   */
  private fetchEmployeeProfile(authId?: AuthId): Observable<EmployeePackage> {
    //* 1.
    return this.fetchData(authId).pipe(
      //* 2.
      map(rawEmployeeData => this.formatEmployeeProfile(rawEmployeeData))
    );
  }

  /**
   ** Refresh EmployeeProfileSubject$ (observable cache) from fetch using AuthId's
   * ___
   * 1. Pipe to SPAuthService <auth>, a wrapper of Auth0Service.
   * ___
   * 2. Filter the AuthId's to only subscribe when AuthIdClaims is filled with data.
   * ___
   * 3. Take 1 to not subscribe until previous observable completes.
   *___
   * 4. ConcatMap the results of HTTP GET request.
   *    - This is done to raise the results nested inner observables to the top stream.
   *___
   * 5. Subscribe to the http get result observable of the newly connected top stream.
   *    - Formatted employeeProfile is then set to Replay Subject
   *    - - Replay Subject used for observable Cache.
   *___
   *
   */
  refresh() {
    return new Promise<EmployeePackage>(resolve => {
      // * 1.
      this.auth.authIds$
        .pipe(
          //* 2.
          filter(authIds => !!authIds),
          //* 3.
          take(1),
          //* 4.
          concatMap(ids => this.fetchEmployeeProfile(ids))
        )
        //* 5.
        .subscribe(
          employeeProfile => (
            this.employeeProfileSubject.next(employeeProfile), resolve(employeeProfile)
          )
        );
    });
  }

  fetchEditPersonalAllowed(): Observable<boolean> {
    return this.auth.authIds$.pipe(
      filter(authIds => !!authIds),
      take(1),
      concatMap(ids => this.fetchEmployeeProfile(ids)),
      map(a => a.personal.portalEditAllowed)
    );
  }
}
